import classNames from 'classnames';
import { ReactNode, useMemo } from 'react';
import { ClassName } from 'types/ClassName';
import DropDownPicker, { IDropDownPickerProps } from '../../DropDownPicker/DropDownPicker';
import SimplePickerRow from '../../SimplePickerRow/SimplePickerRow';

export interface IBasicPickerValue<K extends string = string> {
  id: K;
  name: string;
}

export interface IBasicPickerProps<K extends string = string, T extends IBasicPickerValue<K> = IBasicPickerValue<K>>
  extends Omit<
    IDropDownPickerProps<T, K>,
    'onSelect' | 'loadedAll' | 'valueTemplate' | 'renderRow' | 'isLoading' | 'dataError'
  > {
  onSelect: (value: K | null) => void;
  renderCustomRow?: (row: T, searchValue: string | undefined) => ReactNode;
  className?: ClassName;
}

const BasicPicker = <K extends string, T extends IBasicPickerValue<K>>({
  value,
  data,
  onSelect,
  renderCustomRow,
  searchValue,
  className,
  ...pickerProps
}: IBasicPickerProps<K, T>) => {
  const item = useMemo(() => data.find((model) => model.id === value), [data, value]);

  function handleSelect(model: T | null) {
    onSelect(model?.id ?? null);
  }

  function renderValueTemplate() {
    if (item) {
      return <div className="ellipsis">{item.name}</div>;
    }

    return <></>;
  }

  function renderRow(item: T) {
    if (renderCustomRow) {
      return renderCustomRow(item, searchValue);
    }
    return <SimplePickerRow key={item.id} value={item.name} searchValue={searchValue} />;
  }

  const classes = classNames('basic-picker', className);

  return (
    <DropDownPicker
      {...pickerProps}
      className={classes}
      value={value}
      data={data}
      searchValue={searchValue}
      renderRow={renderRow}
      onSelect={handleSelect}
      valueTemplate={renderValueTemplate()}
      loadedAll={true}
      isLoading={false}
      dataError={false}
    />
  );
};

export default BasicPicker;
